import { DatePipe, LowerCasePipe } from "@angular/common";
import { Component, inject, input, output } from "@angular/core";
import { RouterLink } from "@angular/router";
import { NoOpLink, PopoverDirective, ShowForRoleInDirective } from "@kno2/common/directives";
import { FileSaveService, HttpUtilitiesService } from "@kno2/common/services";
import { NotificationService } from "@kno2/common/services/notifications";
import { MessagesService } from "@kno2/data-access/messages";
import { UserMessageEvent } from "@kno2/data-access/users";
import { DeliverySummarySendToIntakeComponent, MESSAGE_ACTIVITY_TOOLTIP, MessageActivityModalComponent } from "@kno2/features/message";
import { ConfirmationDialogService } from "@kno2/shared/confirmation-dialog";
import { DialogService } from "@kno2/shared/dialog";

@Component({
    selector: "kno2-release-status-cell-content",
    imports: [PopoverDirective, NoOpLink, LowerCasePipe, DatePipe, RouterLink, ShowForRoleInDirective],
    templateUrl: "./release-status-cell-content.component.html",
    styles: `
        .dropdown-menu-absolute {
            position: absolute;
            top: 100%;
            left: 0;
            transform: translate3d(0, 0, 0);
            z-index: 1050; /* Ensure it appears above other elements */
        }
    `
})
export class ReleaseStatusCellContentComponent {
    private readonly messageService = inject(MessagesService);
    private readonly dialogService = inject(DialogService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly notificationService = inject(NotificationService);
    private readonly httpUtilitiesService = inject(HttpUtilitiesService);
    private readonly fileSaveService = inject(FileSaveService);

    public event = input.required<UserMessageEvent>();
    public deliverySummaryEnableDownload = input<boolean>(false);

    public onEventDeleted = output<string>();
    public onDownloadDeliverySummary = output<string>();

    public async downloadDeliverySummary(messageId: string): Promise<void> {
        const { body, headers } = await this.messageService.getDeliverySummary(messageId);
        const fileName = this.httpUtilitiesService.getFileName(headers.get("content-disposition"), "delivery-summary.pdf");
        const fileType = headers.get("content-type");
        await this.fileSaveService.saveFile(body, fileType, fileName);
    }

    public async openSendDeliverySummaryToIntakeModal(messageId: string): Promise<void> {
        await this.dialogService.open(DeliverySummarySendToIntakeComponent, {
            inputValues: { messageId },
            headerText: "Send Delivery Summary to Intake"
        });
    }

    public async deleteEvent(message: UserMessageEvent): Promise<void> {
        await this.confirmationDialogService.open({
            windowClass: "modal-600",
            bodyText: `
                <p>You are about to delete the message or batch with a subject of
                <strong>${message.subject}</strong></p>
                <p>Are you sure?</p>`,
            confirmFn: async () => {
                await this.messageService.deleteMessage(message.id);
            },
            errFn: (error: Record<"message", string>) => this.notificationService.error(error.message),
            successNotificationText: "Message has been deleted"
        });

        this.onEventDeleted.emit(message.id);
    }

    public async showActivity(messageId: string, messagSubject: string): Promise<void> {
        await this.dialogService.open(MessageActivityModalComponent, {
            inputValues: { messageId, messageSubject: messagSubject },
            headerText: "Message Activity",
            headerTooltipText: MESSAGE_ACTIVITY_TOOLTIP,
            styleClass: "audit-message-modal",
            width: "768px"
        });
    }
}
